import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { fmtRelative } from '../lib/constants';
import { Send, Search, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

export default function WhatsAppPage() {
  const { t } = useTranslation();
  const [convs, setConvs] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState('');
  const nav = useNavigate();

  // Stabilizziamo loadList con useCallback così possiamo includerlo
  // nelle deps di useEffect senza rifare la chiamata API in loop.
  const loadList = useCallback(async () => {
    setLoading(true);
    const r = await api.get('/whatsapp/conversations');
    setConvs(r.data);
    setLoading(false);
  }, []);

  const loadThread = async (lead) => {
    setActive(lead);
    const r = await api.get(`/whatsapp/${lead.id}`);
    setThread(r.data);
  };

  useEffect(() => { loadList(); }, [loadList]);

  const send = async () => {
    if (!text.trim() || !active) return;
    if (!active.phone) { toast.error(t('whatsappPage.missingPhone', 'Numero mancante')); return; }
    await api.post('/whatsapp/send', { lead_id: active.id, body: text });
    setText('');
    loadThread(active);
    loadList();
  };

  const filtered = convs.filter((c) =>
    !search || (c.lead.name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex h-full" data-testid="whatsapp-page">
      <div className="w-80 border-r border-border bg-[var(--surface-1)] flex flex-col">
        <div className="p-3 border-b border-border">
          <div className="font-serif text-lg text-[hsl(var(--primary))]">WhatsApp</div>
          <div className="relative mt-2">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-8" placeholder={t('whatsappPage.searchPlaceholder', 'Cerca conversazione…')} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="whatsapp-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="whatsapp-conversation-list">
          {loading && <div className="p-4 text-muted-foreground text-sm">{t('whatsappPage.loading', 'Caricamento…')}</div>}
          {!loading && filtered.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground"><MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-50" />{t('whatsappPage.noConversations', 'Nessuna conversazione.')}</div>}
          {filtered.map((c) => (
            <button
              key={c.lead.id}
              onClick={() => loadThread(c.lead)}
              className={`w-full text-left px-3 py-2.5 border-b border-border hover:bg-[hsl(var(--accent))] ${active?.id === c.lead.id ? 'bg-[hsl(var(--accent))]' : ''}`}
              data-testid={`whatsapp-conv-${c.lead.id}`}
            >
              <div className="flex justify-between items-center">
                <div className="font-medium text-sm truncate">{c.lead.name}</div>
                <div className="text-[11px] text-muted-foreground">{fmtRelative(c.last_message?.created_at)}</div>
              </div>
              <div className="text-xs text-muted-foreground truncate">{c.last_message?.body}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 flex flex-col bg-[var(--surface-2)]">
        {!active ? (
          <div className="flex-1 grid place-items-center text-muted-foreground">
            <div className="text-center">
              <MessageSquare className="w-8 h-8 mx-auto opacity-50 mb-2" />
              <div>{t('whatsappPage.selectConversation', 'Seleziona una conversazione')}</div>
            </div>
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-border bg-[var(--surface-1)] flex items-center justify-between">
              <div>
                <div className="font-medium">{active.name}</div>
                <div className="text-xs text-muted-foreground">{active.phone || 'no phone'}</div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => nav(`/app/leads/${active.id}`)} data-testid="whatsapp-open-lead">{t('whatsappPage.openLead', 'Apri lead')}</Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {thread.map((m) => (
                <div key={m.id} className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`} data-testid="whatsapp-message">
                  <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${m.direction === 'out' ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]' : 'bg-card text-foreground border border-border'}`}>
                    <div>{m.body}</div>
                    <div className="text-[10px] opacity-70 mt-0.5">{fmtRelative(m.created_at)} · {m.status}</div>
                  </div>
                </div>
              ))}
              {thread.length === 0 && <div className="text-center text-sm text-muted-foreground py-6">{t('whatsappPage.noMessages', 'Nessun messaggio.')}</div>}
            </div>
            <div className="p-3 border-t border-border bg-[var(--surface-1)] flex gap-2">
              <Input placeholder={active.phone ? t('whatsappPage.writeMessage', 'Scrivi un messaggio…') : t('whatsappPage.phoneUnavailable', 'Numero non disponibile')} disabled={!active.phone} value={text} onChange={(e) => setText(e.target.value)} data-testid="whatsapp-composer-input" />
              <Button onClick={send} disabled={!text.trim() || !active.phone} data-testid="whatsapp-send-button"><Send className="w-4 h-4" /></Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
